import {useState} from 'react';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import {managementRequest} from 'API/v1/admin/Management';
import {
  fromDateInput,
  isExpired,
  linkName,
  projectedScreens,
  toDateInput
} from 'utils/adminManagement';
import {Capacity} from './ManagementUI';

function useEditor(initial, endpoint, item, onSaved) {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const change = (name, value) => {
    setForm((old) => ({...old, [name]: value}));
    setError(null);
  };
  const field = (name) => ({
    value: form[name],
    onChange: (event) => change(name, event.target.value),
    error: Boolean(error?.fields?.[name]),
    helperText: error?.fields?.[name]
  });
  const submit = async (event, converters = {}) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    const data = {};
    for (const [key, value] of Object.entries(form)) {
      if (key === 'password' && !value && item?.id) continue;
      if (item?.id && value === initial[key] && key !== 'allowOvercommit') continue;
      data[key] = converters[key] ? converters[key](value) : value;
    }
    try {
      const result = await managementRequest(
        `${endpoint}${item?.id ? `/${item.id}` : ''}`,
        data,
        item?.id ? 'patch' : 'post'
      );
      onSaved(result);
    } catch (failure) {
      setError(failure);
      setSaving(false);
    }
  };
  return {form, change, field, submit, error, saving};
}

function SaveActions({saving, onClose, creating}) {
  return (
    <DialogActions sx={{px: 3, pb: 2}}>
      <Button disabled={saving} onClick={onClose}>
        Cancelar
      </Button>
      <Button type="submit" variant="contained" disabled={saving}>
        {saving ? 'Guardando…' : creating ? 'Crear' : 'Guardar cambios'}
      </Button>
    </DialogActions>
  );
}

function Overcommit({editor, assigned, max}) {
  if (assigned <= max && !editor.error?.fields?.allowOvercommit) return null;
  return (
    <Alert severity="warning">
      <Typography variant="body2">
        La asignación supera la capacidad contratada del M3U. Puede provocar límites durante la
        reproducción.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={editor.form.allowOvercommit}
            onChange={(e) => editor.change('allowOvercommit', e.target.checked)}
          />
        }
        label="Permitir el exceso de pantallas"
      />
    </Alert>
  );
}

export function M3UEditor({item, onClose, onSaved}) {
  const initial = {
    name: item?.name || '',
    path: item?.path || '',
    notes: item?.notes || '',
    maxClients: String(item?.maxClients ?? 1),
    expiresAt: toDateInput(item?.expiresAt),
    enabled: item?.enabled ?? true,
    allowOvercommit: false
  };
  const editor = useEditor(initial, '/m3us', item, onSaved);
  const [visible, setVisible] = useState(false);
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={editor.saving ? undefined : onClose}
      aria-labelledby="m3u-editor-title"
    >
      <Box
        component="form"
        sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}
        onSubmit={(event) => editor.submit(event, {maxClients: Number, expiresAt: fromDateInput})}
      >
        <DialogTitle id="m3u-editor-title">
          {item?.id ? `Editar ${linkName(item)}` : 'Añadir enlace M3U'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{pt: 0.5}}>
            {editor.error && <Alert severity="error">{editor.error.message}</Alert>}
            <TextField
              autoFocus
              label="Nombre del enlace"
              placeholder="Proveedor · Cuenta familiar"
              {...editor.field('name')}
              inputProps={{maxLength: 255}}
            />
            <TextField
              required
              label="URL del M3U"
              type={visible ? 'text' : 'password'}
              {...editor.field('path')}
              autoComplete="off"
              inputProps={{maxLength: 8192}}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={visible ? 'Ocultar URL' : 'Mostrar URL'}
                      onClick={() => setVisible(!visible)}
                      edge="end"
                    >
                      {visible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <TextField
                fullWidth
                required
                label="Pantallas permitidas"
                type="number"
                {...editor.field('maxClients')}
                inputProps={{min: 1, max: 100000, step: 1}}
              />
              <TextField
                fullWidth
                required
                label="Caducidad del enlace"
                type="datetime-local"
                {...editor.field('expiresAt')}
                InputLabelProps={{shrink: true}}
              />
            </Stack>
            {item && (
              <Capacity assigned={item.assignedScreens} max={Number(editor.form.maxClients) || 0} />
            )}
            <Overcommit
              editor={editor}
              assigned={item?.assignedScreens || 0}
              max={Number(editor.form.maxClients)}
            />
            <TextField
              label="Notas internas"
              multiline
              minRows={3}
              {...editor.field('notes')}
              inputProps={{maxLength: 10000}}
              placeholder="Renovaciones, contacto del proveedor, observaciones…"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editor.form.enabled}
                  onChange={(e) => editor.change('enabled', e.target.checked)}
                />
              }
              label="Enlace activado"
            />
            {!editor.form.enabled && (
              <Alert severity="warning">
                Los usuarios conservarán la asociación, pero no podrán reproducir con este enlace.
                Se solicitará el cierre de sus sesiones activas.
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              Las fechas se muestran en la zona horaria de este dispositivo. La capacidad suma las
              pantallas máximas de todos los usuarios asociados, también los desactivados o
              caducados.
            </Typography>
          </Stack>
        </DialogContent>
        <SaveActions saving={editor.saving} onClose={onClose} creating={!item?.id} />
      </Box>
    </Dialog>
  );
}

export function UserEditor({item, links, initialM3uId, onClose, onSaved}) {
  const initial = {
    name: item?.name || '',
    email: item?.email || '',
    phone: item?.phone || '',
    password: '',
    enabled: item?.enabled ?? true,
    maxActiveStreams: String(item?.maxActiveStreams ?? 1),
    playerVariant: item?.playerVariant || 'A',
    m3uId: item?.m3uId ?? initialM3uId ?? null,
    m3uExpiresAt: toDateInput(item?.m3uExpiresAt),
    allowOvercommit: false
  };
  const editor = useEditor(initial, '/users', item, onSaved);
  const selected = links.find((link) => link.id === editor.form.m3uId) || null;
  const assigned = projectedScreens(selected, item, editor.form.maxActiveStreams);
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={editor.saving ? undefined : onClose}
      aria-labelledby="user-editor-title"
    >
      <Box
        component="form"
        sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}
        onSubmit={(event) =>
          editor.submit(event, {maxActiveStreams: Number, m3uExpiresAt: fromDateInput})
        }
      >
        <DialogTitle id="user-editor-title">
          {item?.id ? `Editar ${item.name || item.email}` : 'Crear usuario'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{pt: 0.5}}>
            {editor.error && <Alert severity="error">{editor.error.message}</Alert>}
            <Typography variant="subtitle2" color="text.secondary">
              DATOS DEL USUARIO
            </Typography>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <TextField
                fullWidth
                autoFocus
                label="Nombre"
                {...editor.field('name')}
                inputProps={{maxLength: 255}}
              />
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                {...editor.field('email')}
                inputProps={{maxLength: 254}}
              />
            </Stack>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <TextField
                fullWidth
                type="tel"
                label="Teléfono"
                {...editor.field('phone')}
                inputProps={{maxLength: 255}}
              />
              <TextField
                fullWidth
                required={!item?.id}
                type="password"
                label={item?.id ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                autoComplete="new-password"
                {...editor.field('password')}
                inputProps={{minLength: 8, maxLength: 72}}
                helperText={
                  editor.error?.fields?.password ||
                  (item?.id ? 'Déjala vacía para conservar la actual.' : 'Mínimo 8 caracteres.')
                }
              />
            </Stack>
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              SUSCRIPCIÓN Y PANTALLAS
            </Typography>
            <Autocomplete
              options={links}
              value={selected}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(link) =>
                `${linkName(link)} · ${link.assignedScreens}/${link.maxClients} pantallas${
                  !link.enabled ? ' · Desactivado' : isExpired(link.expiresAt) ? ' · Caducado' : ''
                }`
              }
              getOptionDisabled={(link) =>
                link.id !== item?.m3uId && (!link.enabled || isExpired(link.expiresAt))
              }
              onChange={(_, link) => {
                editor.change('m3uId', link?.id ?? null);
                editor.change('allowOvercommit', false);
              }}
              noOptionsText="No hay enlaces que coincidan"
              clearText="Quitar asociación"
              openText="Elegir enlace"
              closeText="Cerrar opciones"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="M3U asociado"
                  error={Boolean(editor.error?.fields?.m3uId)}
                  helperText={
                    editor.error?.fields?.m3uId ||
                    'Busca por nombre. Un usuario puede tener un M3U asociado.'
                  }
                />
              )}
            />
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <TextField
                fullWidth
                required
                label="Pantallas máximas del usuario"
                type="number"
                {...editor.field('maxActiveStreams')}
                inputProps={{min: 0, max: 10000, step: 1}}
              />
              <TextField
                fullWidth
                required={Boolean(selected)}
                disabled={!selected}
                label="Caducidad de la suscripción"
                type="datetime-local"
                {...editor.field('m3uExpiresAt')}
                InputLabelProps={{shrink: true}}
              />
            </Stack>
            {selected && (
              <>
                <Box>
                  <Typography variant="body2" sx={{mb: 1}}>
                    Capacidad del M3U después de guardar
                  </Typography>
                  <Capacity assigned={assigned} max={selected.maxClients} />
                </Box>
                <Overcommit editor={editor} assigned={assigned} max={selected.maxClients} />
                {editor.form.m3uExpiresAt &&
                  new Date(editor.form.m3uExpiresAt) > new Date(selected.expiresAt) && (
                    <Alert severity="warning">
                      El M3U caduca antes que la suscripción del usuario. Tendrás que renovar el
                      enlace o cambiar la asociación para mantener el servicio.
                    </Alert>
                  )}
                {(!selected.enabled || isExpired(selected.expiresAt)) && (
                  <Alert severity="warning">
                    El enlace asociado no está disponible para reproducir.
                  </Alert>
                )}
              </>
            )}
            {item?.m3uId && item.m3uId !== editor.form.m3uId && (
              <Alert severity="info">
                El cambio cerrará las sesiones actuales. El usuario deberá actualizar su lista de
                canales para utilizar la nueva asociación.
              </Alert>
            )}
            <Divider />
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
              <TextField
                select
                fullWidth
                label="Reproductor"
                {...editor.field('playerVariant')}
                SelectProps={{native: true}}
              >
                <option value="A">A · MPEG-TS</option>
                <option value="B">B · Anticortes</option>
                <option value="C">C · Ultra HLS</option>
                <option value="D">D · Congelar imagen</option>
              </TextField>
              <FormControlLabel
                sx={{minWidth: 190}}
                control={
                  <Switch
                    checked={editor.form.enabled}
                    onChange={(e) => editor.change('enabled', e.target.checked)}
                  />
                }
                label="Usuario activado"
              />
            </Stack>
            {editor.form.playerVariant === 'D' && (
              <Alert severity="info">
                El reproductor D requiere la aplicación y la web en versión 0.6.0 o posterior.
              </Alert>
            )}
            {!editor.form.enabled && (
              <Alert severity="warning">
                Se bloqueará la reproducción y se solicitará el cierre de las sesiones activas. Sus
                pantallas seguirán reservadas mientras conserve el M3U asociado.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <SaveActions saving={editor.saving} onClose={onClose} creating={!item?.id} />
      </Box>
    </Dialog>
  );
}

export function ToggleDialog({kind, item, onClose, onSaved}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const name = kind === 'm3us' ? linkName(item) : item.name || item.email;
  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await managementRequest(
        `/${kind}/${item.id}`,
        {enabled: !item.enabled},
        'patch'
      );
      onSaved(result);
    } catch (failure) {
      setError(failure.message);
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="toggle-title"
    >
      <DialogTitle id="toggle-title">
        {item.enabled ? 'Desactivar' : 'Activar'} {name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography>
            {item.enabled
              ? kind === 'm3us'
                ? `Se bloqueará la reproducción de los ${item.userCount} usuarios asociados y se solicitará el cierre de sus sesiones. Las asociaciones y las fechas se conservarán.`
                : 'Se bloqueará la reproducción del usuario y se solicitará el cierre de sus sesiones. Su M3U y sus pantallas reservadas se conservarán.'
              : 'La reproducción estará disponible si el M3U y la suscripción del usuario están vigentes.'}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={item.enabled ? 'warning' : 'primary'}
          disabled={saving}
          onClick={submit}
        >
          {saving ? 'Guardando…' : item.enabled ? 'Desactivar' : 'Activar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SaveActions.propTypes = {
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  creating: PropTypes.bool
};
Overcommit.propTypes = {
  editor: PropTypes.object.isRequired,
  assigned: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired
};
M3UEditor.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};
UserEditor.propTypes = {
  item: PropTypes.object,
  links: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialM3uId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};
ToggleDialog.propTypes = {
  kind: PropTypes.oneOf(['m3us', 'users']).isRequired,
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};
